import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FIELD_GROUPS,
  fieldsInGroup,
  type FieldGroupCode,
  type FieldName,
} from '../lib/field-groups';

type Props = {
  selected: FieldName[];
  onChange: (fields: FieldName[]) => void;
  /**
   * Arvede felter (fx fra forælder-kategori). Vises som markerede + låste —
   * brugeren kan ikke afmarkere dem her, og de tæller med i gruppe-tælleren.
   */
  inherited?: FieldName[];
};

export default function FieldGroupAccordion({ selected, onChange, inherited = [] }: Props) {
  const { t } = useTranslation();
  const inheritedSet = new Set(inherited);
  const [open, setOpen] = useState<Set<FieldGroupCode>>(() => {
    // Ekspandér grupper der har valgte eller arvede felter
    const set = new Set<FieldGroupCode>();
    for (const g of FIELD_GROUPS) {
      if (g.fields.some((f) => selected.includes(f) || inheritedSet.has(f))) set.add(g.code);
    }
    return set;
  });

  const selectedSet = new Set(selected);
  // Et felt er "effektivt valgt" hvis enten brugeren har valgt det ELLER det er arvet
  const isActive = (f: FieldName) => selectedSet.has(f) || inheritedSet.has(f);

  function toggleGroup(code: FieldGroupCode) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function toggleField(field: FieldName) {
    if (inheritedSet.has(field)) return; // Arvede felter kan ikke ændres her
    const next = new Set(selectedSet);
    if (next.has(field)) next.delete(field);
    else next.add(field);
    onChange([...next]);
  }

  function toggleGroupSelection(code: FieldGroupCode) {
    const fields = fieldsInGroup(code);
    // Vi ændrer kun ikke-arvede felter — de arvede er låste
    const editable = fields.filter((f) => !inheritedSet.has(f));
    if (editable.length === 0) return;
    const allEditableSelected = editable.every((f) => selectedSet.has(f));
    const next = new Set(selectedSet);
    if (allEditableSelected) {
      for (const f of editable) next.delete(f);
    } else {
      for (const f of editable) next.add(f);
      setOpen((prev) => new Set(prev).add(code));
    }
    onChange([...next]);
  }

  return (
    <div className="border border-slate-200 rounded divide-y divide-slate-200">
      {FIELD_GROUPS.map((g) => {
        const groupFields = g.fields;
        const activeCount = groupFields.filter((f) => isActive(f)).length;
        const editableFields = groupFields.filter((f) => !inheritedSet.has(f));
        const editableSelectedCount = editableFields.filter((f) => selectedSet.has(f)).length;
        const allEditableLocked = editableFields.length === 0;
        const allActive = activeCount === groupFields.length;
        const someActive = activeCount > 0 && !allActive;
        const isOpen = open.has(g.code);

        return (
          <div key={g.code}>
            <div className="flex items-center px-3 py-2 hover:bg-slate-50">
              <Checkbox
                checked={allActive}
                indeterminate={someActive}
                disabled={allEditableLocked}
                onChange={() => toggleGroupSelection(g.code)}
                aria-label={t(`fieldGroups.${g.code}`)}
              />
              <button
                type="button"
                onClick={() => toggleGroup(g.code)}
                className="flex-1 flex items-center justify-between text-left ml-2"
              >
                <span className="text-sm font-medium text-slate-800">
                  {t(`fieldGroups.${g.code}`)}
                  {activeCount > 0 && (
                    <span className="ml-2 text-xs text-slate-500">
                      ({activeCount} / {groupFields.length})
                    </span>
                  )}
                  {editableSelectedCount === 0 &&
                    activeCount > 0 &&
                    activeCount === groupFields.length && (
                      <span className="ml-1 text-xs text-slate-400 italic">
                        — {t('fieldGroups.allInherited')}
                      </span>
                    )}
                </span>
                <Chevron open={isOpen} />
              </button>
            </div>
            {isOpen && (
              <div className="bg-slate-50 px-3 py-2 pl-10 space-y-1.5">
                {groupFields.map((f) => {
                  const inheritedField = inheritedSet.has(f);
                  return (
                    <label
                      key={f}
                      className={`flex items-center text-sm ${
                        inheritedField ? '' : 'cursor-pointer'
                      }`}
                      title={inheritedField ? t('fieldGroups.inheritedField') : undefined}
                    >
                      <Checkbox
                        checked={isActive(f)}
                        disabled={inheritedField}
                        onChange={() => toggleField(f)}
                        aria-label={t(`field.${f}`)}
                      />
                      <span className={`ml-2 ${inheritedField ? 'text-slate-500' : 'text-slate-700'}`}>
                        {t(`field.${f}`)}
                        {inheritedField && (
                          <span className="ml-1.5 text-xs text-slate-400 italic">
                            {t('fieldGroups.inherited')}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Checkbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  ...rest
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
  'aria-label'?: string;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      ref={(el) => {
        if (el) el.indeterminate = !!indeterminate && !checked;
      }}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      className="rounded border-slate-300 text-slate-900 focus:ring-slate-500 disabled:opacity-60"
      {...rest}
    />
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={`text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`}
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
